import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { PERMISSIONS, lotToUse, type InventoryItemSummary, type InventoryLot } from '@jupi/shared'
import {
  ApiError,
  RemoteContent,
  UseStockDialog,
  useAuth,
  useClubToday,
  useFleetAircraft,
  useInventoryItems,
  useInventoryWrites,
  useSnackbar,
} from '@jupi/ui'

const SHORT = 6

// The stock that fits one aircraft (its registration, its type, or anything),
// as the mechanic asks it at the aircraft: is there one on the shelf, and where.
// "Use" records the part going into this aircraft: one tap, the aircraft already
// chosen, taken from the lot that expires first.
export function FittingParts({ registration }: { registration: string }) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const { can } = useAuth()
  const today = useClubToday()
  const writes = useInventoryWrites()
  const { data: aircraft = [] } = useFleetAircraft()
  const { data: items = [], isLoading, error } = useInventoryItems({ registration })
  const [using, setUsing] = useState<{ item: InventoryItemSummary; lot: InventoryLot } | null>(null)
  const stocked = items.filter((i) => i.usableQuantity > 0 || i.lowStock)
  const canUse = can(PERMISSIONS.inventoryManage)

  return (
    <RemoteContent isLoading={isLoading} error={error} minHeight={80}>
      {stocked.length === 0 ? <Typography color="text.secondary">{t('huolto.partsNone')}</Typography> : null}
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} spacing={1.5}>
        {stocked.slice(0, SHORT).map((item) => {
          const where = [...new Set(item.lots.filter((l) => l.quantity > 0 && l.locationName).map((l) => l.locationName as string))]
          const lot = lotToUse(item.lots, today)
          return (
            <Stack key={item.itemId} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>{item.name}</Typography>
                <Typography color="text.secondary">{[item.partNumber, where.join(', ')].filter(Boolean).join(' · ')}</Typography>
              </Box>
              {item.lowStock ? <Chip size="small" color="warning" label={t('huolto.partsLow')} /> : null}
              <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', whiteSpace: 'nowrap' }}>
                {item.usableQuantity} {item.unit}
              </Typography>
              {canUse && lot ? (
                <Button variant="contained" size="large" onClick={() => setUsing({ item, lot })}>
                  {t('huolto.partsUse')}
                </Button>
              ) : null}
            </Stack>
          )
        })}
      </Stack>
      <Button component={RouterLink} to={`/parts?registration=${encodeURIComponent(registration)}`} sx={{ mt: 1.5 }}>
        {t('huolto.partsAll')}
      </Button>

      <UseStockDialog
        open={using !== null}
        max={using?.lot.quantity ?? 0}
        unit={using?.item.unit ?? ''}
        aircraft={aircraft}
        defaultRegistration={registration}
        saving={writes.useStock.isPending}
        onClose={() => setUsing(null)}
        onUse={async (use) => {
          const target = using
          if (!target) return
          try {
            await writes.useStock.mutateAsync({ lotId: target.lot.lotId, ...use })
            showSnackbar(t('huolto.partsUsed', { name: target.item.name }))
            setUsing(null)
          } catch (err) {
            showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
          }
        }}
      />
    </RemoteContent>
  )
}
